import { PACKS, formatPrice, formatPerScan } from "./packs.js";
import { PublicFooter, ThemeToggle } from "./PublicPage.jsx";
import BrandLockup from "./BrandLockup.jsx";
import LanguageSwitcher from "./LanguageSwitcher.jsx";
import { useT } from "./i18n/I18nContext.jsx";

// FAQ entries reference translation keys (faqQ1/faqA1 .. faqQ4/faqA4) rather
// than hardcoding English, so the list renders in the visitor's language.
const FAQ_KEYS = [
  ["faqQ1", "faqA1"],
  ["faqQ2", "faqA2"],
  ["faqQ3", "faqA3"],
  ["faqQ4", "faqA4"],
];

// The "try it free" CTA opens the camera/file picker directly — no login
// wall first. The picked file is handed up to App.jsx, which carries it
// through the login flow and auto-submits it the moment the account exists,
// so the very first thing a visitor does is take a photo, not fill in a
// form. A real account is still created (free-trial scanning needs
// somewhere to track "has this account used its free scan"), it just
// happens right after the photo instead of before.
function TryItButton({ onPickFile, className, children }) {
  return (
    <label className={className}>
      <input
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPickFile(file);
        }}
      />
      {children}
    </label>
  );
}

export default function LandingPage({
  onGetStarted,
  onNavigate,
  onPickFile,
  theme,
  setTheme,
}) {
  const t = useT();
  return (
    <div className="app landing-app">
      <header className="landing-header">
        <BrandLockup />
        <div className="header-actions">
          <LanguageSwitcher />
          <ThemeToggle theme={theme} setTheme={setTheme} />
          <button className="secondary" onClick={onGetStarted}>
            {t("common.signIn")}
          </button>
        </div>
      </header>

      <main className="landing-main">
        <span className="landing-badge">
          <span className="landing-badge-dot" />
          {t("landing.badge")}
        </span>

        <h1 className="landing-h1">
          {t("landing.h1Line1")}
          <br />
          <span className="landing-accent">{t("landing.h1Accent")}</span>
        </h1>

        <p className="landing-sub">{t("landing.sub")}</p>

        <TryItButton onPickFile={onPickFile} className="primary landing-cta">
          {t("landing.ctaTry")} <span aria-hidden>→</span>
        </TryItButton>
        <p className="landing-cta-caption">{t("landing.ctaCaption")}</p>

        <div className="landing-trust-grid">
          {[
            { titleKey: "landing.trustNoSubTitle", descKey: "landing.trustNoSubDesc" },
            { titleKey: "landing.trustTryTitle", descKey: "landing.trustTryDesc" },
            { titleKey: "landing.trustExpireTitle", descKey: "landing.trustExpireDesc" },
          ].map((x) => (
            <div key={x.titleKey} className="card landing-trust-card">
              <p className="landing-trust-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="landing-check">
                  <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="12" r="9" />
                </svg>
                {t(x.titleKey)}
              </p>
              <p className="landing-trust-desc">{t(x.descKey)}</p>
            </div>
          ))}
        </div>

        <section className="landing-section">
          <h2 className="landing-h2">{t("landing.howTitle")}</h2>
          <div className="landing-steps">
            {[
              { n: "1", titleKey: "landing.step1Title", descKey: "landing.step1Desc" },
              { n: "2", titleKey: "landing.step2Title", descKey: "landing.step2Desc" },
              { n: "3", titleKey: "landing.step3Title", descKey: "landing.step3Desc" },
            ].map((s) => (
              <div key={s.n} className="card landing-step">
                <span className="landing-step-num">{s.n}</span>
                <p className="landing-trust-title">{t(s.titleKey)}</p>
                <p className="landing-trust-desc">{t(s.descKey)}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="landing-section" id="pricing">
          <h2 className="landing-h2">{t("landing.pricingTitle")}</h2>
          <p className="landing-sub landing-section-sub">{t("landing.pricingSub")}</p>
          <div className="pack-grid">
            {PACKS.map((p) => (
              <div key={p.id} className="pack-card">
                {p.badge && <span className="pack-badge">{p.badge}</span>}
                <strong>{p.label}</strong>
                <span className="pack-credits">{p.scans} {t("landing.scansSuffix")}</span>
                <span className="pack-price">{formatPrice(p, "usd")}</span>
                <span className="pack-credits">
                  {formatPerScan(p, "usd")}{t("landing.perScanSuffix")}
                </span>
              </div>
            ))}
          </div>
          <button className="primary landing-cta" onClick={onGetStarted}>
            {t("landing.getStarted")} <span aria-hidden>→</span>
          </button>
          <p className="landing-cta-caption">
            <button className="link-btn" onClick={() => onNavigate("pricing")}>
              {t("landing.seeFullPricing")} →
            </button>
          </p>
        </section>

        <section className="landing-section landing-faq">
          <h2 className="landing-h2">{t("landing.faqTitle")}</h2>
          <div className="landing-faq-list">
            {FAQ_KEYS.map(([qKey, aKey]) => (
              <details key={qKey} className="card landing-faq-item">
                <summary className="landing-faq-q">{t(`landing.${qKey}`)}</summary>
                <p className="landing-trust-desc">{t(`landing.${aKey}`)}</p>
              </details>
            ))}
          </div>
        </section>

        <TryItButton
          onPickFile={onPickFile}
          className="primary landing-cta landing-cta-repeat"
        >
          {t("landing.ctaTry")} <span aria-hidden>→</span>
        </TryItButton>

        <PublicFooter onNavigate={onNavigate} />
        <footer className="landing-footer">
          {t("landing.copyright", { year: new Date().getFullYear() })}
        </footer>
      </main>
    </div>
  );
}
